// Craft Edit Assistant - Smart AI for improving writing craft
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Sparkles, Loader2, AlertCircle, Coins, X } from 'lucide-react';

const QUICK_CRAFT_ACTIONS = [
  { label: 'More Descriptive', instruction: 'Make this text more descriptive with vivid imagery and sensory details' },
  { label: 'Vary Sentences', instruction: 'Vary the sentence length and structure to improve rhythm and pacing' },
  { label: 'Add Depth', instruction: 'Add more depth and detail without changing the story events' },
  { label: 'Natural Dialogue', instruction: 'Make the dialogue sound more natural and realistic' },
  { label: 'Sensory Details', instruction: 'Add more sensory details (sight, sound, smell, taste, touch)' },
  { label: 'Tighten Prose', instruction: 'Tighten the prose by removing unnecessary words and improving clarity' },
];

export function CraftEditAssistant({
  selectedText,
  onEditComplete,
  onClose,
  documentId
}) {
  const [customInstruction, setCustomInstruction] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);

  useEffect(() => {
    loadCreditBalance();
  }, []);

  useEffect(() => {
    // Calculate estimated cost when instruction or text changes
    if (customInstruction && selectedText) {
      calculateCost(customInstruction);
    }
  }, [customInstruction, selectedText]);

  const loadCreditBalance = async () => {
    try {
      setLoadingBalance(true);
      const data = await api.request('/credits/balance');
      setCreditBalance(data.credits || 0);
    } catch (err) {
      console.error('Failed to load credit balance:', err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const calculateCost = async (instruction) => {
    try {
      const data = await api.request('/craft-edit/calculate-cost', {
        method: 'POST',
        body: JSON.stringify({
          selected_text: selectedText,
          instruction: instruction
        })
      });
      setEstimatedCost(data);
    } catch (err) {
      console.error('Failed to calculate cost:', err);
    }
  };

  const handleQuickAction = async (action) => {
    await handleEdit(action.instruction);
  };

  const handleCustomEdit = async () => {
    if (!customInstruction.trim()) {
      setError('Please enter an instruction');
      return;
    }
    await handleEdit(customInstruction);
  };

  const handleEdit = async (instruction) => {
    if (!selectedText || !selectedText.trim()) {
      setError('Please select some text to edit');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      const response = await api.request('/craft-edit/edit', {
        method: 'POST',
        body: JSON.stringify({
          selected_text: selectedText,
          instruction: instruction,
          document_id: documentId
        })
      });

      // Update credit balance
      setCreditBalance(response.credits_remaining);

      // Pass edited text back to parent
      if (onEditComplete) {
        onEditComplete(response.edited_text, response);
      }

    } catch (err) {
      console.error('Craft edit error:', err);
      
      // Handle insufficient credits error
      if (err.status === 402 && err.detail) {
        const detail = typeof err.detail === 'string' ? JSON.parse(err.detail) : err.detail;
        setError(
          `Insufficient credits! You need ${detail.credits_needed || 0} credits but have ${detail.credits_available || 0}. ` +
          `Purchase ${detail.credits_short || 0} more credits to continue.`
        );
      } else {
        setError(err.message || 'Failed to process edit');
      }
      
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white border border-purple-200 rounded-lg shadow-lg p-4">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">✨ AI Craft Editor</h3>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-purple-50 px-3 py-1 rounded-full">
            <Coins className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-900">
              {loadingBalance ? '...' : creditBalance} credits
            </span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={processing}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <Alert className="mb-4 bg-red-50 border-red-200">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-800 text-sm">
            {error}
            {error.includes('Insufficient credits') && (
              <button
                className="ml-2 underline font-semibold"
                onClick={() => window.location.href = '/credits'}
              >
                Buy Credits →
              </button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-3">
          Quick craft improvements:
        </p>
        <div className="grid grid-cols-2 gap-2">
          {QUICK_CRAFT_ACTIONS.map((action) => (
            <Button
              key={action.label}
              onClick={() => handleQuickAction(action)}
              disabled={processing || !selectedText}
              variant="outline"
              size="sm"
              className="text-xs hover:bg-purple-50 hover:border-purple-300"
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">
          Or describe what you want:
        </p>
        <Textarea
          value={customInstruction}
          onChange={(e) => setCustomInstruction(e.target.value)}
          placeholder='e.g., "Make this more suspenseful" or "These sentences are too short"'
          className="min-h-[80px] text-sm"
          disabled={processing}
        />
      </div>

      {estimatedCost && customInstruction && (
        <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
          <span className="text-blue-900">
            Estimated cost: <strong>{estimatedCost.credits_required} credits</strong>
            {' '}({estimatedCost.edit_type})
          </span>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={handleCustomEdit}
          disabled={processing || !customInstruction.trim() || !selectedText}
          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Apply Edit
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-gray-500 mt-3 text-center">
        Craft editing improves <strong>how</strong> you write, not <strong>what</strong> you write
      </p>
    </div>
  );
}
